import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ConvertDetail.css';

// Simple Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, message }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h4>Confirm Conversion</h4>
        <p>{message}</p>
        <div className="modal-actions">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={onConfirm} className="btn-primary">Confirm</button>
        </div>
      </div>
    </div>
  );
};

// USSD Code Component - Just a simple link
const USSDCode = ({ code }) => {
  return (
    <div className="ussd-code">
      <a href={`tel:${code}`} className="ussd-link">
        {code}
      </a>
      <div className="ussd-hint">
        Click/Tap to dial on mobile • On desktop: copy and dial manually
      </div>
    </div>
  );
};

function ConvertDetail() {
  const { type } = useParams();
  const navigate = useNavigate();
  
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState(0);
  const [expectedAmount, setExpectedAmount] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  
  // Set conversion type details
  useEffect(() => {
    if (!['safaricom', 'airtel', 'bonga'].includes(type)) {
      navigate('/convert');
      return;
    }
    
    switch(type) {
      case 'safaricom':
        setRate(0.9);
        break;
      case 'airtel':
        setRate(0.8);
        break;
      case 'bonga':
        setRate(0.6);
        break;
      default:
        setRate(0.0);
    }
  }, [type, navigate]);

  // Calculate expected amount
  useEffect(() => {
    if (amount && !isNaN(amount)) {
      setExpectedAmount((parseFloat(amount) * rate).toFixed(2));
    } else {
      setExpectedAmount(0);
    }
  }, [amount, rate]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10,12}$/.test(phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid 10-12 digit phone number';
    }
    
    if (!amount) {
      newErrors.amount = 'Amount is required';
    } else if (isNaN(amount) || parseFloat(amount) < 30) {
      newErrors.amount = 'Amount must be at least 30';
    } else if (parseFloat(amount) > 10000) {
      newErrors.amount = 'Amount cannot exceed 10,000';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsConfirming(true);
    }
  };

  const handleConfirm = () => {
    setIsConfirming(false);
    setIsSubmitting(true);
    
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      setTimeout(() => {
        setPhoneNumber('');
        setAmount('');
        setSubmitSuccess(false);
        navigate('/convert'); 
      }, 30000);
    }, 3000);
  };

  const handleCancel = () => {
    setIsConfirming(false);
  };

  const getConversionTitle = () => {
    switch(type) {
      case 'safaricom':
        return 'Convert Safaricom Airtime to Money';
      case 'airtel':
        return 'Convert Airtel Airtime to Money';
      case 'bonga':
        return 'Convert Bonga Points to Money';
      default:
        return 'Convert to Money';
    }
  };

  const getConfirmationMessage = () => {
    return `Are you sure you want to convert ${amount} ${type === 'bonga' ? 'Bonga points' : `${type} airtime`} for phone number ${phoneNumber}? You will receive approximately Ksh.${expectedAmount}.`;
  };

  return (
    <div className="convert-detail">
      <div className="container">
        <button className="back-btn" onClick={() => navigate('/convert')}>
          &larr; Back
        </button>
        
        <h1>{getConversionTitle()}</h1>
        
        <div className="info-box">
          {type === 'safaricom' && <p>Swap your Safaricom Airtime for Money at ~ 90.00% rate.</p>}
          {type === 'airtel' && <p>Swap your Airtel Airtime for Money at ~ 80.00% rate.</p>}
          {type === 'bonga' && <p>Swap your Bonga points for Money at competitive rates.</p>}
        </div>
        
        <div className="form-box">
          {submitSuccess ? (
            <div className="success-box">
              <h3>✓ Conversion Request Submitted!</h3>
              <p>
                To convert {amount} {
                  type === 'safaricom' 
                    ? `Safaricom airtime, dial:`
                    : type === 'airtel' 
                    ? `Airtel airtime, dial:`
                    : type === 'bonga' 
                    ? `Bonga points, dial:`
                    : ''
                }
              </p>
              
              <USSDCode 
                code={
                  type === 'safaricom' ? `*140*${amount}*0180254208#` :
                  type === 'airtel' ? `*140*${amount}*0750922115#` :
                  type === 'bonga' ? `*126*9*0759061855*${amount}#` : ''
                }
              />
              
              <p className="amount-info">You will receive <strong>Ksh.{expectedAmount}</strong> in your M-Pesa account</p>
            </div>
          ) : (
            <form className="conversion-form" onSubmit={handleFormSubmit}>
              <div className="form-group">
                <label>
                  {type === 'safaricom' ? 'Safaricom phone number' : type === 'airtel' ? 'Airtel phone number' : type === 'bonga' ? 'Safaricom phone number' : 'Phone number'}
                </label>
                <input 
                  type="tel"
                  placeholder="e.g., 0712345678" 
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className={errors.phoneNumber ? 'error' : ''}
                  maxLength="12"
                />
                {errors.phoneNumber && <div className="error">{errors.phoneNumber}</div>}
              </div>
              
              <div className="form-group">
                <label>Amount to convert</label>
                <input 
                  type="number" 
                  placeholder="Min=30, Max=10000" 
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className={errors.amount ? 'error' : ''}
                  min="30"
                  max="10000"
                />
                {errors.amount && <div className="error">{errors.amount}</div>}
              </div>
              
              <div className="amount-preview">
                You will receive: <strong>Ksh.{expectedAmount}</strong>
              </div>
              
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </div>
      
      <ConfirmationModal 
        isOpen={isConfirming}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        message={getConfirmationMessage()}
      />
    </div>
  );
}

export default ConvertDetail;
